import React, { useState, useEffect, useRef } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import toast from 'react-hot-toast';
import EmailFormHandler from '../forms/EmailFormHandler';

interface ContactFormProps {
  initialReason?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ initialReason = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
  });
  
  const [formStatus, setFormStatus] = useState<{type: string; message: string} | null>(null);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    // Set initial subject based on reason if provided
    if (initialReason) {
      let subject = '';
      
      if (initialReason.startsWith('formation-')) {
        subject = 'Formation';
        setFormData(prev => ({
          ...prev,
          message: `Je souhaite obtenir plus d'informations sur votre formation.`
        }));
      } else if (initialReason === 'essai') {
        subject = 'Essai Gratuit';
        setFormData(prev => ({
          ...prev,
          message: `Je souhaite réserver un essai gratuit pour une moto électrique.`
        }));
      } else if (initialReason === 'information') {
        subject = 'Information Produit';
      }
      
      if (subject) {
        setFormData(prev => ({ ...prev, subject }));
      }
    }
  }, [initialReason]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (!captchaValue) {
      toast.error('Veuillez valider le captcha');
      return;
    }
    
    setFormStatus({ type: 'loading', message: 'Envoi en cours...' });
    
    // Form will be submitted by EmailFormHandler
    // This is just for UI feedback
    
    // Reset reCAPTCHA
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setCaptchaValue(null);
    
    // Set success status
    setFormStatus({
      type: 'success',
      message: 'Votre message a été envoyé avec succès à hello@proxyemobility.com. Nous vous contacterons bientôt!'
    });
    
    // Reset form data
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      subject: '',
      message: '',
    });
    
    // Clear status after 5 seconds
    setTimeout(() => {
      setFormStatus(null);
    }, 5000);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Envoyez-nous un Message</h3>
      
      {formStatus && (
        <div 
          className={`p-3 sm:p-4 mb-4 sm:mb-6 rounded-md text-sm sm:text-base flex items-start ${
            formStatus.type === 'success' ? 'bg-green-50 text-green-700' : 
            formStatus.type === 'loading' ? 'bg-blue-50 text-blue-700' : 
            'bg-red-50 text-red-700'
          }`}
        >
          {formStatus.type === 'error' && <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />}
          {formStatus.message}
        </div>
      )}
      
      <EmailFormHandler formName="Formulaire de Contact" className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nom Complet *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="John Doe"
              required
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Adresse Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="john@example.com"
              required
            />
          </div>
          
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
              Entreprise
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="Votre Entreprise"
            />
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="+221 77 123 45 67"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
            Sujet *
          </label>
          <select
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            required
          >
            <option value="" disabled>Sélectionnez un sujet</option>
            <option value="Information Produit">Information Produit</option>
            <option value="Service Après-Vente">Service Après-Vente</option>
            <option value="Partenariat">Partenariat</option>
            <option value="Recrutement">Recrutement</option>
            <option value="Essai Gratuit">Essai Gratuit</option>
            <option value="Formation">Formation</option>
            <option value="Investissement">Investissement</option>
            <option value="Autre">Autre</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 h-24 sm:h-32 text-sm"
            placeholder="Comment pouvons-nous vous aider?"
            required
          />
        </div>
        
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // This is a test key that always passes validation
            onChange={handleCaptchaChange}
          />
        </div>
        
        <button
          type="submit"
          className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md transition-colors flex items-center justify-center text-sm"
          disabled={formStatus?.type === 'loading' || !captchaValue}
        >
          {formStatus?.type === 'loading' ? 'Envoi en cours...' : 'Envoyer le Message'}
          <Send size={16} className="ml-2" />
        </button>
      </EmailFormHandler>
    </div>
  );
};

export default ContactForm;